/* ----------------------------------------------------------------------------
 * HeroStateWave — the animated audio-wave that flows BEHIND the dashboard hero
 * text while Aura is Listening (teal) or Speaking (warm orange/coral/magenta).
 * There is NO wave for idle/thinking/error — the component returns null, so the
 * animation unmounts the instant the state changes (no lingering motion).
 *
 * It is a single absolutely-positioned, aria-hidden, pointer-events:none layer:
 * a 200%-wide layered SVG whose <g> groups drift left by -50% forever. Only
 * transform animates (GPU); the wave never affects layout and is clipped by the
 * hero card's own overflow:hidden. prefers-reduced-motion renders the same
 * strokes at a STATIC offset (no motion). Driven purely by `status`.
 * -------------------------------------------------------------------------- */
import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import {
  HERO_WAVE_CSS,
  HERO_WAVE_STYLE_ID,
  STATE_ACCENTS,
  heroWaveActive,
  type VoiceStatus,
} from './stateVisualStyle.ts';

function ensureStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(HERO_WAVE_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = HERO_WAVE_STYLE_ID;
  el.textContent = HERO_WAVE_CSS;
  document.head.appendChild(el);
}

/* Logical canvas: one tile = 1200 wide; the SVG spans two tiles (2400) so a
 * -50% (one-tile) drift loops seamlessly. Integer periods-per-tile keep the
 * seam invisible. Paths are constant, so build them once at module load. */
const TILE = 1200;
const SPAN = TILE * 2;
const MID = 120;
const STEP = 12;

interface LayerSpec {
  periods: number;   // integer periods PER TILE (seamless at the -50% loop)
  amp: number;
  yOffset: number;
  phase: number;
  width: number;
  opacity: number;
  durationS: number;
}

const LAYERS: LayerSpec[] = [
  { periods: 2, amp: 34, yOffset: -6, phase: 0.0, width: 2.2, opacity: 0.6, durationS: 17 },
  { periods: 3, amp: 24, yOffset: 8, phase: 1.7, width: 1.6, opacity: 0.42, durationS: 23 },
  { periods: 4, amp: 16, yOffset: 2, phase: 3.4, width: 1.3, opacity: 0.3, durationS: 29 },
];

function buildPath(spec: LayerSpec): string {
  const base = MID + spec.yOffset;
  const k = (2 * Math.PI * spec.periods) / TILE;
  let d = '';
  for (let x = 0; x <= SPAN; x += STEP) {
    const y = base + spec.amp * Math.sin(k * x + spec.phase);
    d += (x === 0 ? 'M' : 'L') + x.toFixed(0) + ' ' + y.toFixed(1) + ' ';
  }
  return d.trim();
}

const LAYER_PATHS: string[] = LAYERS.map(buildPath);

export interface HeroStateWaveProps {
  status: VoiceStatus;
  className?: string;
}

export function HeroStateWave({ status, className }: HeroStateWaveProps) {
  ensureStyles();
  useEffect(() => { ensureStyles(); }, []);

  // No decorative wave for idle / thinking / error — unmount immediately.
  if (!heroWaveActive(status)) return null;

  const colors = STATE_ACCENTS[status].heroColors;

  return (
    <div className={cn('bc-hero-wave', className)} aria-hidden>
      <svg viewBox={`0 0 ${SPAN} ${MID * 2}`} preserveAspectRatio="none" fill="none">
        {LAYERS.map((layer, i) => (
          <g
            key={i}
            className="bc-hw-layer"
            style={{ animationDuration: `${layer.durationS}s` }}
          >
            <path
              d={LAYER_PATHS[i]}
              stroke={colors[i % colors.length]}
              strokeWidth={layer.width}
              strokeLinecap="round"
              strokeOpacity={layer.opacity}
              vectorEffect="non-scaling-stroke"
            />
          </g>
        ))}
      </svg>
    </div>
  );
}
